import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import SearchFilters from './SearchFilters';
import StudentResults from './StudentResults';
import StudentDetail from './StudentDetail';
import LoaderOverlay from '../../components/LoaderOverlay';
import styles from '../../styles/AsignacionGruposStyles/AsignacionGrupos.module.css';
import estudiantesStyles from '../../styles/EstudiantesStyles/Estudiantes.module.css';
import programasStyles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';

// APIs
import { getStudents, getStudent, getStudentCreditsSummary } from '../../api/students';
import { getCampus } from '../../api/campus';
import { getCycles } from '../../api/ciclosEscolares';
import { getGruposEstudiante, asignarEstudianteGrupo, desasignarEstudianteGrupo, getSiguienteCicloEstudiante } from '../../api/estudiantesGrupos';
import { getPreasignacionesPorEstudiante } from '../../api/preasignaciones';
import { getMateriasAsignables } from '../../api/groups';

// Detectar el ciclo escolar actual (el que está corriendo hoy)
function detectarCicloActual(ciclosList) {
  if (!ciclosList || ciclosList.length === 0) return null;
  const hoy = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  
  // 1. Buscar ciclo con estado_ciclo ACTIVO cuyas fechas contengan hoy
  let ciclo = ciclosList.find(c => 
    c.estado_ciclo === 'ACTIVO' && c.fecha_inicio && c.fecha_fin &&
    c.fecha_inicio <= hoy && c.fecha_fin >= hoy
  );
  if (ciclo) return ciclo;
  
  // 2. Buscar cualquier ciclo cuyas fechas contengan hoy
  ciclo = ciclosList.find(c => 
    c.fecha_inicio && c.fecha_fin &&
    c.fecha_inicio <= hoy && c.fecha_fin >= hoy &&
    c.activo !== false
  );
  if (ciclo) return ciclo;
  
  // 3. Fallback: ciclo con estado_ciclo ACTIVO más reciente
  ciclo = ciclosList
    .filter(c => c.estado_ciclo === 'ACTIVO')
    .sort((a, b) => (b.fecha_inicio || '').localeCompare(a.fecha_inicio || ''))[0];
  if (ciclo) return ciclo;
  
  // 4. Fallback: ciclo activo más reciente que ya inició
  ciclo = ciclosList
    .filter(c => c.activo !== false && c.fecha_inicio && c.fecha_inicio <= hoy)
    .sort((a, b) => (b.fecha_inicio || '').localeCompare(a.fecha_inicio || ''))[0];
  return ciclo || null;
}

function normalizarTipoPeriodo(valor) {
  if (!valor) return null;
  const texto = String(valor).trim().toUpperCase();
  if (!texto) return null;
  if (texto.includes('TETRA')) return 'TETRAMESTRAL';
  if (texto.includes('SEMEST')) return 'SEMESTRAL';
  return texto;
}

function AsignacionGruposScreen() {
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    campus: '',
    cicloEscolar: ''
  });
  
  // Estados para datos del backend
  const [students, setStudents] = useState([]);
  const [campusList, setCampusList] = useState([]);
  const [ciclosList, setCiclosList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Estados para asignación de grupos
  const [preasignaciones, setPreasignaciones] = useState([]);
  const [gruposAsignados, setGruposAsignados] = useState([]);
  const [gruposDisponibles, setGruposDisponibles] = useState([]);
  const [gruposSeleccionados, setGruposSeleccionados] = useState([]);
  const [inscripcionActual, setInscripcionActual] = useState(null);
  const [siguienteCiclo, setSiguienteCiclo] = useState(null);

  // Cargar catálogos iniciales
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        console.log('[AsignacionGruposScreen] Cargando catálogos...');
        
        const [campusData, ciclosData] = await Promise.all([
          getCampus(),
          getCycles()
        ]);
        
        setCampusList(campusData || []);
        setCiclosList(ciclosData || []);
        
        // Establecer ciclo más reciente como default
        if (ciclosData && ciclosData.length > 0) {
          const cicloMasReciente = ciclosData[0]; // Backend devuelve ordenado DESC
          setFilters(prev => ({ ...prev, cicloEscolar: cicloMasReciente.id || cicloMasReciente.id_ciclo_escolar }));
        }
      } catch (err) {
        console.error('[AsignacionGruposScreen] Error cargando catálogos:', err);
        setError('Error al cargar los catálogos');
      }
    };
    
    loadCatalogs();
  }, []);

  // Sincronizar selección de campus desde HeaderGlobal (localStorage y evento campusChanged)
  useEffect(() => {
    let mounted = true;

    const applyCampusFromStorage = async () => {
      const newCampus = localStorage.getItem('selectedCampus') || '';
      if (!mounted) return;
      if (!newCampus) {
        setFilters(prev => ({ ...prev, campus: '' }));
        return;
      }
      if (newCampus === 'Todos') {
        setFilters(prev => ({ ...prev, campus: 'Todos' }));
        return;
      }

      // Buscar en la lista de campus cargada
      let campusObj = campusList.find(c => c.nombre === newCampus || c.nombre_campus === newCampus || String(c.id) === String(newCampus));
      if (!campusObj) {
        try {
          const refreshed = await getCampus();
          if (!mounted) return;
          setCampusList(refreshed || []);
          campusObj = (refreshed || []).find(c => c.nombre === newCampus || c.nombre_campus === newCampus || String(c.id) === String(newCampus));
        } catch (e) {
          // ignore
        }
      }

      if (campusObj && campusObj.id) {
        setFilters(prev => ({ ...prev, campus: campusObj.id }));
      } else {
        // Si no se encontró por nombre, dejar el valor en vacío para no filtrar
        setFilters(prev => ({ ...prev, campus: '' }));
      }
    };

    const handleStorage = () => {
      applyCampusFromStorage();
    };

    const handleCampusChanged = (e) => {
      applyCampusFromStorage();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('campusChanged', handleCampusChanged);

    // Aplicar inicialmente
    applyCampusFromStorage();

    return () => {
      mounted = false;
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('campusChanged', handleCampusChanged);
    };
  }, [campusList]);

  // Cargar estudiantes según filtros
  const loadStudents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = { no_pagination: true };
      if (searchTerm) {
        params.search = searchTerm;
      }
      if (filters.campus && filters.campus !== 'Todos') {
        params.id_campus = filters.campus;
      }
      if (filters.cicloEscolar && filters.cicloEscolar !== 'Todos') {
        params.id_ciclo_escolar = filters.cicloEscolar;
      }
      
      const data = await getStudents(params);
      let studentsData = Array.isArray(data) ? data : (data.results || []);
      
      // Detectar ciclo actual (el que está corriendo hoy)
      const cicloActual = detectarCicloActual(ciclosList);
      const cicloActualNombre = cicloActual?.nombre_ciclo || cicloActual?.name || null;
      const cicloSeleccionado = (ciclosList || []).find(c =>
        String(c.id_ciclo_escolar || c.id) === String(filters.cicloEscolar)
      );
      const cicloSeleccionadoNombre = cicloSeleccionado?.nombre_ciclo || cicloSeleccionado?.name || null;
      
      // Mapear datos para la tabla
      const mappedStudents = studentsData.map(s => ({
        id: s.id_estudiante,
        id_estudiante: s.id_estudiante,
        nombre: s.nombre_completo || 'N/A',
        matricula: s.matricula || 'N/A',
        campus: s.campus_nombre || s.id_campus?.nombre_campus || 'N/A',
        campus_id: s.id_campus?.id_campus || s.id_campus,
        programa: s.programa_nombre || s.id_programa_actual?.nombre_programa || 'N/A',
        programa_id: s.id_programa_actual?.id_programa || s.id_programa_actual,
        cicloEscolar: cicloSeleccionadoNombre || s.ciclo_escolar_nombre || s.ciclo_ingreso?.nombre_ciclo || 'N/A',
        ciclo_id: s.ciclo_ingreso?.id_ciclo_escolar || s.ciclo_ingreso,
        periodoActual: (s.estatus_estudiante === 'ACTIVO' || !s.estatus_estudiante) ? (cicloActualNombre || s.periodo_inicial || 'N/A') : (s.periodo_inicial || 'N/A'),
        periodo_inicial: s.periodo_inicial,
        cicloActualId: cicloActual?.id_ciclo_escolar || null,
        seccion: s.seccion_nombre || 'N/A',
        seccion_id: s.seccion_id || s.id_seccion_id || null,
        institucion: s.institucion_nombre || 'Universidad Lux',
        creditos_acumulados: null,
        creditos_totales: null,
        porcentaje_avance: null,
        estatus: s.estatus_estudiante || 'ACTIVO',
        _raw: s
      }));
      
      setStudents(mappedStudents);
    } catch (err) {
      console.error('[AsignacionGruposScreen] Error cargando estudiantes:', err);
      setError('Error al cargar los estudiantes');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, ciclosList]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  useEffect(() => {
    const handleInscripcionesUpdated = () => {
      loadStudents();
    };

    window.addEventListener('inscripciones:updated', handleInscripcionesUpdated);
    return () => {
      window.removeEventListener('inscripciones:updated', handleInscripcionesUpdated);
    };
  }, [loadStudents]);

  // Refresca los créditos del estudiante seleccionado cuando se guardan calificaciones
  const selectedStudentId = selectedStudent?.id_estudiante || selectedStudent?.id || null;
  useEffect(() => {
    if (!selectedStudentId) return undefined;

    const handleCalificacionesUpdated = async (event) => {
      const idsActualizados = event?.detail?.ids_estudiante || [];
      if (!idsActualizados.map(String).includes(String(selectedStudentId))) return;

      const resumen = await getStudentCreditsSummary(selectedStudentId);
      setSelectedStudent(prev => {
        if (!prev || String(prev.id_estudiante || prev.id) !== String(selectedStudentId)) return prev;
        return {
          ...prev,
          creditos_acumulados: resumen?.creditos_acumulados ?? null,
          creditos_totales: resumen?.creditos_totales ?? null,
          porcentaje_avance: resumen?.porcentaje_avance ?? null,
        };
      });
    };

    window.addEventListener('calificaciones:updated', handleCalificacionesUpdated);
    return () => {
      window.removeEventListener('calificaciones:updated', handleCalificacionesUpdated);
    };
  }, [selectedStudentId]);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Obtener inscripción actual del estudiante
  const obtenerInscripcionActual = useCallback(async (estudianteId) => {
    if (!estudianteId) return null;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE || 'http://localhost:8000/api'}/escolar/estudiantes/${estudianteId}/detalle-inscripcion/`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        return data.inscripcion_actual || null;
      }
    } catch (error) {
      console.error('[AsignacionGruposScreen] Error obteniendo inscripción:', error);
    }
    return null;
  }, []);

  const handleStudentSelect = async (student) => {
    try {
      setLoadingDetail(true);
      setError(null);
      setSuccessMessage(null);
      
      console.log('=== [AsignacionGrupos] INICIO SELECCIÓN ESTUDIANTE ===');
      console.log('[AsignacionGrupos] Estudiante inicial:', student);
      
      // Obtener detalle completo del estudiante
      let studentDetail = student;
      if (student.id_estudiante || student.id) {
        try {
          const fullStudent = await getStudent(student.id_estudiante || student.id);
          console.log('[AsignacionGrupos] Detalle completo del backend:', fullStudent);
          
          // Detectar ciclo actual para mostrar como periodo actual
          const cicloActual = detectarCicloActual(ciclosList);
          const esActivo = (fullStudent.estatus_estudiante === 'ACTIVO' || !fullStudent.estatus_estudiante);
          const cicloActualNombre = cicloActual?.nombre_ciclo || cicloActual?.name || null;
          
          studentDetail = {
            ...student,
            ...fullStudent,
            id: fullStudent.id_estudiante || student.id,
            id_estudiante: fullStudent.id_estudiante || student.id_estudiante,
            nombre: fullStudent.nombre_completo || student.nombre,
            matricula: fullStudent.matricula || student.matricula,
            campus: fullStudent.campus_nombre || fullStudent.id_campus?.nombre_campus || student.campus,
            campus_id: fullStudent.id_campus?.id_campus || fullStudent.id_campus_id || student.campus_id,
            programa: fullStudent.programa_nombre || fullStudent.id_programa_actual?.nombre_programa || student.programa,
            programa_id: fullStudent.id_programa_actual?.id_programa || fullStudent.id_programa_actual_id || student.programa_id,
            periodoActual: esActivo ? (cicloActualNombre || fullStudent.periodo_inicial || student.periodoActual || 'N/A') : (fullStudent.periodo_inicial || student.periodoActual || 'N/A'),
            periodo_inicial: fullStudent.periodo_inicial || student.periodo_inicial,
            seccion: fullStudent.seccion_nombre || student.seccion || 'N/A',
            seccion_id: fullStudent.seccion_id || fullStudent.id_seccion_id || student.seccion_id || null,
            institucion: fullStudent.institucion_nombre || student.institucion || 'Universidad Lux',
            ciclo_id: fullStudent.ciclo_ingreso?.id_ciclo_escolar || fullStudent.ciclo_ingreso_id || student.ciclo_id,
            cicloActualId: cicloActual?.id_ciclo_escolar || null,
          };
        } catch (err) {
          console.warn('[AsignacionGrupos] No se pudo cargar detalle completo:', err);
        }
      }
      
      const estudianteId = studentDetail.id_estudiante || studentDetail.id;
      const programaId = studentDetail.programa_id;
      const campusId = studentDetail.campus_id;
      const periodoEstudiante = studentDetail.periodo_inicial || 1;
      const seccionId = studentDetail.seccion_id || studentDetail._raw?.seccion_id || studentDetail._raw?.id_seccion_id || null;
      const cicloEstudianteId = studentDetail.ciclo_id || studentDetail.cicloActualId || null;
      const cicloEstudiante = (ciclosList || []).find(c =>
        String(c.id_ciclo_escolar || c.id) === String(cicloEstudianteId)
      );
      const tipoPeriodoEstudiante = normalizarTipoPeriodo(
        studentDetail?._raw?.id_programa_actual?.tipo_periodo_nombre ||
        studentDetail?._raw?.id_programa_actual?.nombre_tipo_periodo ||
        cicloEstudiante?.tipo_periodo_nombre
      );
      
      console.log('[AsignacionGrupos] IDs extraídos:');
      console.log('  - estudianteId:', estudianteId);
      console.log('  - programaId:', programaId);
      console.log('  - campusId:', campusId);
      console.log('  - periodoEstudiante:', periodoEstudiante);
      console.log('  - seccionId:', seccionId);
      console.log('  - tipoPeriodoEstudiante:', tipoPeriodoEstudiante);
      
      // 1. Cargar preasignaciones del estudiante
      const preasigs = await getPreasignacionesPorEstudiante(estudianteId);
      console.log('[AsignacionGrupos] 1. Preasignaciones encontradas:', preasigs?.length || 0, preasigs);
      setPreasignaciones(preasigs || []);
      
      // 2. Cargar grupos ya asignados al estudiante
      const gruposAsig = await getGruposEstudiante(estudianteId);
      console.log('[AsignacionGrupos] 2. Grupos ya asignados:', gruposAsig?.length || 0);
      console.log('[AsignacionGrupos] 2. Detalle de grupos asignados:', gruposAsig?.map(g => ({
        id_estudiante_grupo: g.id_estudiante_grupo,
        materia: g.materia_nombre,
        grupo: g.grupo_codigo,
        activo: g.activo,
        estado: g.estado,
        horarios: g.horarios
      })) || []);
      setGruposAsignados(gruposAsig || []);

      // 2b. Obtener el ciclo SIGUIENTE del estudiante (para previsualización de
      // horario y detección de traslapes). Ya NO se usa para restringir los
      // grupos disponibles: el servicio backend devuelve grupos de cualquier
      // ciclo en estado PLANEADO/ACTIVO.
      const ciclosEstudiante = await getSiguienteCicloEstudiante(estudianteId);
      setSiguienteCiclo(ciclosEstudiante?.ciclo_siguiente || null);
      
      // 3. Cargar inscripción actual
      const inscripcion = await obtenerInscripcionActual(estudianteId);
      console.log('[AsignacionGrupos] 3. Inscripción actual:', inscripcion);
      setInscripcionActual(inscripcion);
      
      // 4. Obtener materias asignables + grupos disponibles desde el servicio
      //    orquestador del backend (DESACOPLADO de Preasignación). La fuente de
      //    verdad académica es el Kardex oficial: solo se ocultan las materias
      //    ya APROBADAS. El ciclo del grupo ya no importa: basta con que su
      //    ciclo escolar esté en estado PLANEADO o ACTIVO.
      const asignablesParams = {};
      if (campusId) asignablesParams.id_campus = campusId;
      if (seccionId) asignablesParams.id_seccion = seccionId;
      if (tipoPeriodoEstudiante) asignablesParams.tipo_periodo = tipoPeriodoEstudiante;

      const asignablesResp = await getMateriasAsignables(estudianteId, asignablesParams);
      const materiasAsignables = asignablesResp?.materias || [];
      const gruposDisp = asignablesResp?.grupos_disponibles || [];
      console.log('[AsignacionGrupos] 4. Materias asignables (no aprobadas):', materiasAsignables.length);
      console.log('[AsignacionGrupos] 4. Grupos disponibles (ciclos PLANEADO/ACTIVO):', gruposDisp.length);
      
      // 5. Identificar materias pendientes de asignación.
      // Regla nueva: una materia se considera "ya asignada" (bloqueada
      // para reasignación) SOLO si tiene una asignación activa cuyo
      // resultado académico no sea reprobatorio.
      //   - aprobada === true   -> acreditada, NUNCA reaparece
      //   - aprobada === null   -> en curso (sin calificación final aún),
      //                            NO se reasigna (ya está cursando)
      //   - aprobada === false  -> reprobada, SÍ puede reaparecer si no
      //                            existe otra asignación activa/aprobada
      // Fuente de verdad: campo `aprobada` del serializer, basado en
      // esc_cardex (tipo_curso ORDINARIO/EXTRAORDINARIO, calificacion_final
      // >= 7). Fallback a esc_calificaciones si la asignación aún no está
      // sincronizada en cardex.
      const materiasBloqueadas = new Set();
      const materiasReprobadas = new Set();
      (gruposAsig || []).forEach(g => {
        const idMat = Number(g.id_materia || g.materia_id);
        if (!idMat) return;
        const aprobada = g.aprobada;
        const estaActiva = g.activo !== false && g.estado !== 'RETIRADO';
        if (aprobada === true) {
          // Acreditada: bloqueada para siempre.
          materiasBloqueadas.add(idMat);
        } else if (aprobada === null && estaActiva) {
          // En curso, sin calificación final: no reasignar.
          materiasBloqueadas.add(idMat);
        } else if (aprobada === false) {
          materiasReprobadas.add(idMat);
        }
      });
      // Una materia reprobada cuya asignación NO esté bloqueada por otra
      // asignación activa/aprobada queda disponible para volverse a asignar.
      materiasReprobadas.forEach(idMat => {
        if (materiasBloqueadas.has(idMat)) {
          materiasReprobadas.delete(idMat);
        }
      });
      // Mantener nombre legacy `materiasConGrupo` apuntando a las bloqueadas
      // para no romper el resto del flujo (es lo que evita duplicados).
      const materiasConGrupo = materiasBloqueadas;

      // Preasignaciones: SOLO como preselección visual. Ya NO filtran la lista
      // de materias ni de grupos; la lista proviene del Kardex/plan completo.
      const setPreasignadas = new Set(preasigs.map(p => Number(p.id_materia)));

      console.log('[AsignacionGrupos] 5. Análisis de materias:');
      console.log('  - Materias bloqueadas (acreditadas o en curso):', [...materiasBloqueadas]);
      console.log('  - Materias reprobadas reasignables:', [...materiasReprobadas]);
      console.log('  - Materias preasignadas (preselección visual):', [...setPreasignadas]);
      
      // 6. Los grupos disponibles ya vienen resueltos por el servicio backend
      //    (materias asignables + grupos de ciclos PLANEADO/ACTIVO, filtrados
      //    por campus/sección/tipo de periodo). No se requiere búsqueda adicional
      //    en el cliente ni restricción por ciclo específico.
      console.log('[AsignacionGrupos] 6. TOTAL grupos disponibles (backend):', gruposDisp?.length || 0);
      setGruposDisponibles(gruposDisp);
      setGruposSeleccionados([]);
      
      // Helper: encontrar la asignación "vigente" de una materia.
      // Preferimos: aprobada=true > en curso (aprobada=null) > reprobada.
      // Las asignaciones reprobadas no se exponen como "grupoAsignado"
      // para que la materia se renderice como pendiente y se muestren
      // los grupos disponibles para reasignar.
      const findGrupoVigente = (idMateria) => {
        const id = Number(idMateria);
        const candidatos = (gruposAsig || []).filter(
          g => Number(g.id_materia || g.materia_id) === id
        );
        if (!candidatos.length) return undefined;
        const aprobada = candidatos.find(g => g.aprobada === true);
        if (aprobada) return aprobada;
        const enCurso = candidatos.find(
          g => g.aprobada === null && g.activo !== false && g.estado !== 'RETIRADO'
        );
        if (enCurso) return enCurso;
        // No exponer reprobadas como asignación actual.
        return undefined;
      };

      // Preparar datos del estudiante con materias para mostrar.
      // Fuente: materias asignables del backend (Kardex-authoritative). Solo se
      // excluyeron las APROBADAS; EN_CURSO, REPROBADA y SIN_CURSAR se muestran.
      const materiasConGrupoDisponible = new Set(
        (gruposDisp || []).map(g => Number(g.id_materia || g._raw?.id_materia))
      );
      const materiasParaMostrar = materiasAsignables.map(m => {
        const idMat = Number(m.id_materia);
        const estatus = (m.estatus || '').toUpperCase();
        return {
          id: idMat,
          id_materia: idMat,
          nombre: m.nombre || m.materia_nombre || 'N/A',
          clave: m.clave || m.materia_clave || '',
          creditos: (m.creditos !== undefined && m.creditos !== null) ? m.creditos : 'N/A',
          periodo_sugerido: m.periodo_sugerido || null,
          estatus: m.estatus || null,
          tieneGrupo: materiasConGrupo.has(idMat),
          grupoAsignado: findGrupoVigente(idMat),
          tieneGrupoDisponible: (typeof m.tiene_grupo_disponible === 'boolean')
            ? m.tiene_grupo_disponible
            : materiasConGrupoDisponible.has(idMat),
          esReprobada: estatus === 'REPROBADA' || materiasReprobadas.has(idMat),
          esPreasignada: setPreasignadas.has(idMat),
          esDelPlan: true,
        };
      });

      studentDetail.materiasPreasignadas = materiasParaMostrar;
      studentDetail.materiasDelPlan = materiasAsignables;
      studentDetail.gruposAsignados = gruposAsig;

      // 7. Créditos oficiales (resumen del Kardex). Si no hay datos, quedan en null → N/A
      const resumenCreditos = estudianteId
        ? await getStudentCreditsSummary(estudianteId)
        : null;
      studentDetail.creditos_acumulados = resumenCreditos?.creditos_acumulados ?? null;
      studentDetail.creditos_totales = resumenCreditos?.creditos_totales ?? null;
      studentDetail.porcentaje_avance = resumenCreditos?.porcentaje_avance ?? null;
      
      console.log('=== [AsignacionGrupos] RESUMEN FINAL ===');
      console.log('Materias para mostrar en UI:', materiasParaMostrar?.length || 0, materiasParaMostrar);
      console.log('Grupos disponibles para seleccionar:', gruposDisp?.length || 0);
      console.log('Estudiante preparado:', studentDetail);
      console.log('=== [AsignacionGrupos] FIN SELECCIÓN ===');
      
      setSelectedStudent(studentDetail);
    } catch (err) {
      console.error('[AsignacionGrupos] Error al seleccionar estudiante:', err);
      setError('Error al cargar datos del estudiante');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleBackToSearch = () => {
    setSelectedStudent(null);
    setPreasignaciones([]);
    setGruposAsignados([]);
    setGruposDisponibles([]);
    setGruposSeleccionados([]);
    setInscripcionActual(null);
    setError(null);
    setSuccessMessage(null);
  };

  // Manejar selección de grupo
  const handleGrupoToggle = (grupoId) => {
    setGruposSeleccionados(prev => {
      if (prev.includes(grupoId)) {
        return prev.filter(id => id !== grupoId);
      } else {
        return [...prev, grupoId];
      }
    });
  };

  // Detectar traslapes de horario entre los grupos seleccionados y los ya
  // asignados (validación previa a guardar). Devuelve mensajes legibles.
  const detectarConflictosHorario = () => {
    const DIA_IDX = { lunes: 0, martes: 1, miercoles: 2, 'miércoles': 2, jueves: 3, viernes: 4, sabado: 5, 'sábado': 5, domingo: 6 };
    const toMin = (str) => {
      if (!str) return null;
      const p = String(str).split(':');
      const h = parseInt(p[0], 10);
      const m = parseInt(p[1] || '0', 10);
      return Number.isNaN(h) ? null : h * 60 + m;
    };
    const dayIdx = (v) => (typeof v === 'number' ? v : DIA_IDX[String(v).toLowerCase()]);
    const bloques = [];

    // Solo se comparan los grupos asignados del CICLO SIGUIENTE (los de ciclos
    // anteriores no forman parte del horario que se está armando).
    const cicloSiguienteId = siguienteCiclo?.id_ciclo_escolar ?? null;

    // Grupos ya asignados
    (gruposAsignados || []).forEach((g) => {
      if (cicloSiguienteId != null && String(g.id_ciclo_escolar_grupo) !== String(cicloSiguienteId)) {
        return;
      }
      (g.horarios || []).forEach((h) => {
        bloques.push({ dia: dayIdx(h.dia), ini: toMin(h.hora_inicio), fin: toMin(h.hora_fin), label: `${g.materia_nombre || 'Materia'}${g.grupo_codigo ? ` (${g.grupo_codigo})` : ''}` });
      });
    });

    // Grupos seleccionados (de los disponibles)
    const selSet = new Set((gruposSeleccionados || []).map(String));
    (gruposDisponibles || []).forEach((g) => {
      if (!selSet.has(String(g.id))) return;
      const horario = (g._raw && g._raw.horario) || {};
      const ini = toMin(horario.hora_inicio);
      const fin = toMin(horario.hora_fin);
      (Array.isArray(horario.dias) ? horario.dias : []).forEach((d) => {
        bloques.push({ dia: dayIdx(d), ini, fin, label: `${g.materia || 'Materia'}${g.codigo_grupo ? ` (${g.codigo_grupo})` : ''}` });
      });
    });

    const mensajes = [];
    const vistos = new Set();
    for (let i = 0; i < bloques.length; i++) {
      for (let j = i + 1; j < bloques.length; j++) {
        const a = bloques[i];
        const b = bloques[j];
        if (a.dia !== b.dia || a.ini == null || a.fin == null || b.ini == null || b.fin == null) continue;
        if (a.ini < b.fin && b.ini < a.fin) {
          const clave = [a.label, b.label].sort().join('|') + a.dia;
          if (!vistos.has(clave)) {
            vistos.add(clave);
            mensajes.push(`• ${a.label} y ${b.label}`);
          }
        }
      }
    }
    return mensajes;
  };

  // Manejar asignación de grupos
  const handleAsignar = async () => {
    if (gruposSeleccionados.length === 0) {
      setError('Debe seleccionar al menos un grupo para asignar');
      return;
    }

    // Validación de horario (C4): detectar traslapes entre los grupos
    // seleccionados y los ya asignados. Se alerta pero se permite continuar.
    const conflictos = detectarConflictosHorario();
    if (conflictos.length > 0) {
      const proceder = window.confirm(
        'Se detectaron posibles traslapes de horario:\n\n' +
        conflictos.join('\n') +
        '\n\n¿Desea continuar con la asignación de todos modos?'
      );
      if (!proceder) {
        return;
      }
    }

    setLoading(true);
    setError(null);
    
    try {
      const estudianteId = selectedStudent.id_estudiante || selectedStudent.id;
      const resultados = [];
      
      for (const grupoId of gruposSeleccionados) {
        try {
          const resultado = await asignarEstudianteGrupo({
            id_estudiante: estudianteId,
            id_grupo: grupoId,
            id_inscripcion: inscripcionActual?.id_inscripcion || null,
            tipo_asignacion: 'MANUAL',
            estado: 'INSCRITO'
          });
          resultados.push({ grupoId, success: true, data: resultado });
        } catch (err) {
          resultados.push({ grupoId, success: false, error: err.message });
        }
      }
      
      const exitosos = resultados.filter(r => r.success).length;
      const fallidos = resultados.filter(r => !r.success).length;
      
      // SIEMPRE recargar datos para sincronizar con backend
      // Incluso si hay errores, el backend puede haber actualizado estados
      console.log('[AsignacionGruposScreen] Recargar datos después de intentos:', { exitosos, fallidos });
      await handleStudentSelect(selectedStudent);
      
      if (exitosos > 0 && fallidos === 0) {
        setSuccessMessage(`Se asignaron ${exitosos} grupo(s) correctamente.`);
      } else if (exitosos > 0 && fallidos > 0) {
        setSuccessMessage(`Se asignaron ${exitosos} grupo(s) correctamente. ${fallidos} grupo(s) no pudieron asignarse (posiblemente ya estaban asignados).`);
        setError('Algunos grupos no pudieron asignarse: ' + resultados.filter(r => !r.success).map(r => r.error).join('; '));
      } else {
        setError('No se pudo asignar ningún grupo. Detalles: ' + resultados.map(r => r.error).join('; '));
      }
    } catch (err) {
      console.error('[AsignacionGruposScreen] Error al asignar grupos:', err);
      setError('Error al asignar grupos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelar = () => {
    handleBackToSearch();
  };

  // Manejar desasignación de un grupo
  const handleDesasignar = async (idEstudianteGrupo, materiaInfo) => {
    const estudianteId = selectedStudent.id_estudiante || selectedStudent.id;
    
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const resultado = await desasignarEstudianteGrupo(idEstudianteGrupo, estudianteId);
      
      const nombreMateria = materiaInfo || resultado.materia_nombre || 'grupo';
      setSuccessMessage(`Se removió la asignación de "${nombreMateria}" correctamente.`);
      
      // Recargar datos del estudiante para reflejar cambios
      await handleStudentSelect(selectedStudent);
    } catch (err) {
      console.error('[AsignacionGruposScreen] Error al desasignar:', err);
      setError('Error al desasignar: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Función para formatear modalidad
  const formatModalidad = (modalidad) => {
    if (!modalidad || modalidad === 'N/A') return 'N/A';
    const modalidadMap = {
      'PRESENCIAL': 'Presencial',
      'EN_LINEA': 'En línea',
      'VIRTUAL_HIBRIDA': 'Híbrida'
    };
    return modalidadMap[modalidad] || modalidad;
  };

  return (
    <div className={styles.layout}>
      <div className={styles.sidebarColumn}>
        <Sidebar />
      </div>
      <div className={styles.contentColumn}>
        <HeaderGlobal title="Asignación de Grupos" />
        <div className={styles.contentArea}>
          <div className={styles.contentWrapper} style={{ position: 'relative', minHeight: 'calc(100vh - 64px)', height: 'auto' }}>
            {!selectedStudent ? (
              <>
                <div className={estudiantesStyles.searchSection}>
                  <div className={estudiantesStyles.searchCard}>
                    <div className={estudiantesStyles.searchRow}>
                      {/* Campus filter removed - HeaderGlobal now controls campus selection */}
                      {/* Buscador */}
                      {/* Ciclo Escolar (moved before buscador) */}
                      <div className={estudiantesStyles.formGroup}>
                        <label className={estudiantesStyles.searchLabel}>Ciclo Escolar</label>
                        <select
                          className={estudiantesStyles.searchSelect}
                          value={filters.cicloEscolar}
                          onChange={e => handleFilterChange('cicloEscolar', e.target.value)}
                        >
                          <option value="">Todos los ciclos</option>
                          {[...ciclosList].sort((a, b) => {
                            const getAnio = c => Number(c.anio || (c.nombre_ciclo ? c.nombre_ciclo.split('-')[0] : (c.name ? c.name.split('-')[0] : '0')));
                            const getPeriodo = c => Number((c.numero_periodo) || (c.nombre_ciclo ? c.nombre_ciclo.split('-')[1] : (c.name ? c.name.split('-')[1] : '0')));
                            const anioA = getAnio(a);
                            const anioB = getAnio(b);
                            if (anioA !== anioB) return anioB - anioA;
                            return getPeriodo(b) - getPeriodo(a);
                          }).map(ciclo => (
                            <option key={ciclo.id_ciclo_escolar} value={ciclo.id_ciclo_escolar}>{ciclo.nombre_ciclo}</option>
                          ))}
                        </select>
                      </div>
                      <div className={estudiantesStyles.searchInputGroup}>
                        <svg className={estudiantesStyles.searchIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="#898d8d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <input
                          type="text"
                          className={estudiantesStyles.searchInput}
                          placeholder="Buscar Estudiante por Matrícula o Nombre"
                          value={searchTerm}
                          onChange={e => setSearchTerm(e.target.value)}
                          onKeyPress={e => { if (e.key === 'Enter') handleSearch(searchTerm); }}
                        />
                      </div>
                      {/* Botón BUSCAR */}
                      <button className={programasStyles.buscarButtonProgramas} onClick={() => handleSearch(searchTerm)}>
                        Buscar
                      </button>
                    </div>
                  </div>
                </div>
                <LoaderOverlay visible={loading} />
                {error ? (
                  <div className={styles.errorContainer}>
                    <p>{error}</p>
                    <button onClick={loadStudents}>Reintentar</button>
                  </div>
                ) : (
                  <StudentResults 
                    students={students}
                    onStudentSelect={handleStudentSelect}
                  />
                )}
              </>
            ) : loadingDetail ? (
              <div className={styles.loadingContainer}>
                <p>Cargando datos del estudiante...</p>
              </div>
            ) : (
              <StudentDetail 
                student={selectedStudent}
                onBack={handleBackToSearch}
                onAsignar={handleAsignar}
                onCancelar={handleCancelar}
                onDesasignar={handleDesasignar}
                gruposDisponibles={gruposDisponibles}
                gruposSeleccionados={gruposSeleccionados}
                onGrupoToggle={handleGrupoToggle}
                inscripcionActual={inscripcionActual}
                siguienteCiclo={siguienteCiclo}
                loading={loading}
                error={error}
                successMessage={successMessage}
                formatModalidad={formatModalidad}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AsignacionGruposScreen;
